import { useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { navigate } from 'gatsby-plugin-intl'

import Button from '@atoms/Button'
import Link from '@atoms/Link'
import Modal from '@molecules/Modal'
import TextInput from '@molecules/TextInput'
import CheckboxIcon from '@icons/Checkbox'
import MailConfirmModal from '@src/components/MailConfirmModal'
import {
  buildGoogleLink,
  confirmMail,
  forgetPassword,
  getUserDetail,
  getUserQuick,
  resendConfirmMail,
  resetPassword,
  signIn,
  signUp,
  updateLanguage,
  updateSubscription,
} from '@models/User'
import { getBookmark, moveBookmark } from '@models/Bookmark'
import { StoreContext } from '@models/Store'
import { useTranslation } from '@models/Translation'
import { sleep } from '@models/Util'
import { sendError } from '@models/Sentry'
import { getLocalData, removeLocalData } from '@models/LocalData'

import * as style from './style.module.sass'

type State = 'default' | 'confirmMail'
type FormData = {
  email: string
  password: string
  subscribe: boolean
  agree: boolean
}

type AuthFormProps = {
  type: 'signup' | 'signin'
}
const AuthForm = ({ type }: AuthFormProps) => {
  const { action } = useContext(StoreContext)
  const { t, locale } = useTranslation()

  const [errorMessage, setErrorMessage] = useState('')
  const [state, setState] = useState<State>('default')
  const [loading, setLoading] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)

  const methods = useForm<FormData>({
    defaultValues: { subscribe: true },
  })
  const { register, handleSubmit, watch, formState, setError } = methods
  const email = watch('email')
  const password = watch('password')
  const subscribe = watch('subscribe')
  const agree = watch('agree')
  const { errors } = formState
  const submit = async (data: FormData) => {
    setLoading(true)
    setErrorMessage('')

    try {
      type === 'signup'
        ? await signUp(data.email, data.password)
        : await signIn(data.email, data.password)
    } catch (error) {
      if (error !== null && typeof error === 'object' && 'name' in error) {
        switch (error.name) {
          case 'UserNotFoundException':
            setErrorMessage(t('mypage.noEmail'))
            setLoading(false)
            return
          case 'UsernameExistsException':
            setErrorMessage(t('mypage.alreadyEmail'))
            setLoading(false)
            return
          case 'NotAuthorizedException':
            setErrorMessage(t('mypage.checkIdPass'))
            setLoading(false)
            return
          case 'InvalidPasswordException':
            setError('password', {
              type: 'manual',
              message: 'InvalidPasswordException',
            })
            setLoading(false)
            return
          case 'UserNotConfirmedException':
            setState('confirmMail')
            resendConfirmMail(email)
            return
        }
      }

      sendError(error)
      action.addToasts(t('mypage.failRequest'))
      setLoading(false)
      return
    }

    if (type === 'signup') {
      setState('confirmMail')
      return
    }

    loadUserInfo(false)
  }

  const confirmedMail = async (code: string) => {
    try {
      await confirmMail(email, code)
    } catch (error) {
      action.addToasts(t('mypage.failCode'))
      sendError(error)
      return
    }

    try {
      await signIn(email, password)
    } catch (error) {
      action.addToasts(t('mypage.failRequest'))
      sendError(error)
      return
    }

    await loadUserInfo(type === 'signup')
  }

  const loadUserInfo = async (isSignup: boolean) => {
    const user = await getUserQuick()
    if (!user.isSignIn) {
      action.addToasts(t('mypage.failRequest'))
      setLoading(false)
      return
    }
    action.setUser(user)

    const redirect = getLocalData('redirect')
    removeLocalData('redirect')
    if (redirect !== undefined) {
      navigate(type === 'signup' ? `/thanks?redirect=${redirect}` : redirect)
    } else {
      navigate(type === 'signup' ? '/thanks' : '/')
    }
    action.reload()

    getUserDetail(user).then(action.setUser)
    getBookmark(user.isSignIn, 1, 200).then(bookmark =>
      action.updateBookmark(bookmark.ids),
    )

    try {
      if (isSignup) {
        try {
          await updateLanguage(locale)
          action.setUser({ ...user, locale })
          updateSubscription(subscribe)
        } catch (error) {
          sendError(error)
          // メルマガ設定だけなので、一旦握りつぶす
        }
      }

      await moveBookmark()
      getBookmark(user.isSignIn, 1, 200).then(bookmark =>
        action.updateBookmark(bookmark.ids),
      )
    } catch (error) {
      sendError(error)
      // 移動させることは重要じゃないので握りつぶす
    }
  }

  return (
    <>
      <Modal>
        <FormProvider {...methods}>
          <form className={style.form} onSubmit={handleSubmit(submit)}>
            <h2>
              {type === 'signup'
                ? t('mypage.registrationFree')
                : t('mypage.login')}
            </h2>
            <div className={style.google}>
              <a href={buildGoogleLink(locale)}>
                <svg width="231" height="51" viewBox="0 0 231 51" fill="none">
                  <rect
                    x="0.745117"
                    y="0.867188"
                    width="229"
                    height="49"
                    rx="7.5"
                    stroke="black"
                  />
                  <mask
                    id="mask0_10307_59464"
                    style={{ maskType: 'luminance' }}
                    maskUnits="userSpaceOnUse"
                    x="16"
                    y="14"
                    width="23"
                    height="23"
                  >
                    <path
                      d="M38.2451 14.3672H16.2451V36.3672H38.2451V14.3672Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_10307_59464)">
                    <path
                      d="M37.8051 25.6172C37.8051 24.8372 37.7352 24.0872 37.6051 23.3672H27.2451V27.6222H33.1651C32.9101 28.9972 32.1352 30.1622 30.9702 30.9422V33.7022H34.5251C36.6051 31.7872 37.8051 28.9672 37.8051 25.6172Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M27.2451 36.367C30.2151 36.367 32.705 35.382 34.525 33.7021L30.97 30.9421C29.9851 31.6021 28.725 31.992 27.2451 31.992C24.38 31.992 21.9551 30.057 21.09 27.457H17.415V30.307C19.2251 33.902 22.9451 36.367 27.2451 36.367Z"
                      fill="#34A853"
                    />
                    <path
                      d="M21.0901 27.4572C20.8701 26.7972 20.7451 26.0922 20.7451 25.3672C20.7451 24.6422 20.8701 23.9372 21.0901 23.2772V20.4272H17.4151C16.6702 21.9122 16.2451 23.5923 16.2451 25.3672C16.2451 27.1422 16.6702 28.8222 17.4151 30.3072L21.0901 27.4572Z"
                      fill="#FBBC04"
                    />
                    <path
                      d="M27.2451 18.7422C28.86 18.7422 30.31 19.2972 31.4501 20.3872L34.6051 17.2321C32.7001 15.4572 30.21 14.3672 27.2451 14.3672C22.9451 14.3672 19.2251 16.8322 17.415 20.4272L21.09 23.2772C21.9551 20.6772 24.38 18.7422 27.2451 18.7422Z"
                      fill="#E94235"
                    />
                  </g>
                  <path
                    d="M55.5372 27.2732L57.5532 27.0632C57.6092 27.6932 57.6932 27.9732 57.9452 28.2952C58.3512 28.8272 59.0792 29.1212 59.9892 29.1212C61.3892 29.1212 62.2292 28.4912 62.2292 27.4412C62.2292 26.8672 61.9632 26.3912 61.5292 26.1532C61.1092 25.9292 61.0252 25.9152 59.0932 25.4672C58.0432 25.2292 57.4832 25.0052 56.9792 24.6132C56.2652 24.0532 55.9012 23.2832 55.9012 22.3452C55.9012 20.3992 57.4692 19.1392 59.8772 19.1392C61.4732 19.1392 62.6772 19.6292 63.3632 20.5532C63.7132 21.0152 63.8812 21.4352 64.0492 22.1772L62.0472 22.4152C61.9632 21.9392 61.8652 21.7012 61.6272 21.4212C61.2632 20.9872 60.6472 20.7492 59.8772 20.7492C58.6592 20.7492 57.8332 21.3652 57.8332 22.2752C57.8332 22.8632 58.1412 23.2692 58.7852 23.5492C59.0932 23.6752 59.2052 23.7032 60.7592 24.0532C61.8652 24.2912 62.5372 24.5572 63.0412 24.9072C63.8392 25.4952 64.2032 26.2652 64.2032 27.3152C64.2032 28.3092 63.8952 29.1212 63.3212 29.6952C62.5792 30.4092 61.4732 30.7592 59.9752 30.7592C58.8272 30.7592 57.7072 30.4792 57.0352 30.0312C56.4752 29.6532 56.0692 29.1212 55.8032 28.4352C55.6772 28.1132 55.6212 27.8472 55.5372 27.2732ZM65.783 19.3912H67.841V21.1692H65.783V19.3912ZM65.783 22.4992H67.827C67.757 23.0172 67.729 23.4792 67.729 24.3332V28.7012C67.729 29.4712 67.757 29.9472 67.827 30.5352H65.783C65.867 29.9052 65.895 29.4152 65.895 28.7152V24.3192C65.895 23.5492 65.867 23.0732 65.783 22.4992ZM69.4158 31.8232L70.8158 30.8572C71.3618 31.6132 71.9778 31.9212 72.9858 31.9212C74.0638 31.9212 74.8898 31.5432 75.2818 30.8712C75.5198 30.4512 75.6178 29.9612 75.6458 29.0092C74.9458 29.8912 74.1198 30.2832 72.9018 30.2832C70.6758 30.2832 69.2478 28.7572 69.2478 26.3632C69.2478 23.9412 70.7178 22.3732 72.9858 22.3732C74.2318 22.3732 75.0578 22.7512 75.6598 23.6052C75.6318 23.3252 75.6318 23.2972 75.6318 23.1432V22.4992H77.5078C77.4378 23.1012 77.4098 23.4932 77.4098 24.2492V28.9392C77.4098 30.6192 77.0458 31.6972 76.2338 32.3832C75.5338 32.9572 74.3718 33.2792 72.9298 33.2792C71.2918 33.2792 70.3678 32.9012 69.4158 31.8232ZM71.0818 26.4052C71.0818 27.2312 71.3058 27.9172 71.7258 28.3372C72.0758 28.7152 72.6778 28.9672 73.2098 28.9672C73.9798 28.9672 74.7638 28.5192 75.1558 27.8612C75.4218 27.4272 75.5478 26.9092 75.5478 26.2652C75.5478 25.3692 75.2678 24.6692 74.7078 24.1932C74.3158 23.8712 73.8538 23.6892 73.3498 23.6892C71.9498 23.6892 71.0818 24.7392 71.0818 26.4052ZM81.2932 30.5352H79.2772C79.3612 29.9332 79.3892 29.4992 79.3892 28.7152V24.3192C79.3892 23.5492 79.3612 23.0732 79.2772 22.4992H81.1812V23.2412C81.1812 23.4512 81.1812 23.4932 81.1532 23.8432C82.0072 22.7512 82.7492 22.3592 84.0092 22.3592C85.0872 22.3592 85.8852 22.7092 86.3892 23.3952C86.7252 23.8572 86.8652 24.4172 86.8652 25.3552V28.7152C86.8652 29.4852 86.8932 30.0172 86.9772 30.5352H84.9472C85.0172 29.9052 85.0452 29.4292 85.0452 28.7152V25.6072C85.0452 25.0192 84.9752 24.6412 84.8072 24.3752C84.5832 24.0112 84.1632 23.8012 83.6312 23.8012C82.6232 23.8012 81.7972 24.5012 81.1812 25.8872V28.7152C81.1812 29.4012 81.2092 29.9332 81.2932 30.5352ZM93.3592 19.3912H95.4172V21.1692H93.3592V19.3912ZM93.3592 22.4992H95.4032C95.3332 23.0172 95.3052 23.4792 95.3052 24.3332V28.7012C95.3052 29.4712 95.3332 29.9472 95.4032 30.5352H93.3592C93.4432 29.9052 93.4712 29.4152 93.4712 28.7152V24.3192C93.4712 23.5492 93.4432 23.0732 93.3592 22.4992ZM99.176 30.5352H97.16C97.244 29.9332 97.272 29.4992 97.272 28.7152V24.3192C97.272 23.5492 97.244 23.0732 97.16 22.4992H99.064V23.2412C99.064 23.4512 99.064 23.4932 99.036 23.8432C99.89 22.7512 100.632 22.3592 101.892 22.3592C102.97 22.3592 103.768 22.7092 104.272 23.3952C104.608 23.8572 104.748 24.4172 104.748 25.3552V28.7152C104.748 29.4852 104.776 30.0172 104.86 30.5352H102.83C102.9 29.9052 102.928 29.4292 102.928 28.7152V25.6072C102.928 25.0192 102.858 24.6412 102.69 24.3752C102.466 24.0112 102.046 23.8012 101.514 23.8012C100.506 23.8012 99.68 24.5012 99.064 25.8872V28.7152C99.064 29.4012 99.092 29.9332 99.176 30.5352ZM119.656 30.5352H118.032C117.976 30.1992 117.85 29.7512 117.682 29.2052L116.604 25.9012C116.576 25.8452 116.492 25.5372 116.338 24.9632C116.268 25.2152 116.1 25.7892 116.058 25.9292L114.98 29.2052C114.826 29.6812 114.672 30.2412 114.616 30.5352H112.992C112.908 30.1292 112.852 29.9192 112.628 29.2052L111.088 24.2352C110.892 23.5912 110.696 23.0172 110.5 22.4992H112.572C112.6 22.9052 112.726 23.6192 112.88 24.1652L113.65 26.8952C113.706 27.0772 113.79 27.4692 113.902 28.0152C113.93 27.8752 114.168 27.0492 114.224 26.8812L115.232 23.8292C115.372 23.4232 115.54 22.7792 115.582 22.4992H117.08C117.122 22.7652 117.304 23.4232 117.43 23.8292L118.466 27.0072C118.508 27.1332 118.578 27.3992 118.662 27.6932C118.704 27.8752 118.718 27.8892 118.746 28.0152C118.774 27.9032 118.774 27.9032 118.816 27.7072C118.858 27.5112 118.956 27.0632 118.984 26.9652L119.768 24.1652C119.936 23.5632 120.048 22.9472 120.076 22.4992H122.134C121.952 22.9612 121.924 23.0732 121.56 24.2352L120.006 29.2052C119.796 29.8772 119.726 30.1432 119.656 30.5352ZM123.137 19.3912H125.195V21.1692H123.137V19.3912ZM123.137 22.4992H125.181C125.111 23.0172 125.083 23.4792 125.083 24.3332V28.7012C125.083 29.4712 125.111 29.9472 125.181 30.5352H123.137C123.221 29.9052 123.249 29.4152 123.249 28.7152V24.3192C123.249 23.5492 123.221 23.0732 123.137 22.4992ZM127.875 20.3432H129.877C129.821 20.8332 129.779 21.4352 129.779 22.0652V22.4992H130.367C131.025 22.4992 131.333 22.4852 131.781 22.4152V24.0672C131.319 23.9972 131.053 23.9832 130.409 23.9832H129.779V28.3232C129.779 29.0092 130.031 29.2612 130.703 29.2612C131.067 29.2612 131.291 29.1912 131.725 28.9532L131.977 30.4372C131.375 30.6612 130.955 30.7312 130.227 30.7312C128.617 30.7312 127.973 30.1572 127.973 28.6872V23.9832H127.441C126.923 23.9832 126.657 23.9972 126.195 24.0672V22.4152C126.601 22.4852 126.867 22.4992 127.455 22.4992H127.973V22.0652C127.973 21.4632 127.931 20.8472 127.875 20.3432ZM133.022 19.3912H135.01C134.94 19.9232 134.912 20.4412 134.912 21.2392V23.2412C134.912 23.4232 134.912 23.4932 134.884 23.8432C135.752 22.7512 136.508 22.3592 137.782 22.3592C139.574 22.3592 140.596 23.3812 140.596 25.1872V28.7152C140.596 29.4852 140.624 30.0172 140.708 30.5352H138.678C138.748 29.9052 138.776 29.4292 138.776 28.7152V25.6072C138.776 24.9632 138.636 24.5012 138.37 24.2212C138.118 23.9552 137.782 23.8152 137.404 23.8152C136.354 23.8152 135.542 24.4872 134.912 25.8872V28.7152C134.912 29.4012 134.94 29.9332 135.024 30.5352H133.008C133.092 29.9332 133.12 29.5132 133.12 28.7152V21.2252C133.12 20.3852 133.092 19.9512 133.022 19.3912ZM156.344 26.1952V27.6652C156.344 28.4912 156.358 28.8692 156.442 29.4852C155.448 30.0312 154.944 30.2552 154.202 30.4512C153.572 30.6192 152.83 30.7172 152.144 30.7172C150.394 30.7172 149.05 30.1712 148.168 29.1072C147.314 28.0992 146.852 26.5872 146.852 24.8512C146.852 22.6252 147.636 20.9172 149.092 19.9372C149.96 19.3632 150.912 19.1112 152.2 19.1112C153.474 19.1112 154.636 19.5452 155.42 20.3152C155.91 20.7912 156.162 21.1972 156.456 21.9812L154.538 22.4992C154.076 21.2252 153.404 20.7772 151.962 20.7772C151.136 20.7772 150.366 21.0852 149.834 21.6312C149.134 22.3592 148.798 23.3952 148.798 24.8232C148.798 26.2652 149.12 27.3572 149.75 28.0992C150.324 28.7712 151.08 29.0652 152.186 29.0652C153.138 29.0652 153.852 28.8832 154.566 28.4352V26.1532H153.81C153.096 26.1532 152.802 26.1672 152.382 26.2372V24.4312C152.886 24.5012 153.194 24.5152 154.062 24.5152H154.79C155.434 24.5152 155.84 24.5012 156.442 24.4592C156.372 24.9772 156.344 25.4112 156.344 26.1952ZM162 30.8012C159.438 30.8012 157.814 29.1492 157.814 26.5312C157.814 23.9272 159.438 22.2612 161.986 22.2612C164.52 22.2612 166.13 23.9132 166.13 26.5032C166.13 29.0932 164.492 30.8012 162 30.8012ZM161.986 23.7172C160.516 23.7172 159.62 24.7812 159.62 26.5312C159.62 28.2532 160.516 29.3172 161.972 29.3172C163.428 29.3172 164.324 28.2672 164.324 26.5592C164.324 24.7532 163.47 23.7172 161.986 23.7172ZM171.379 30.8012C168.817 30.8012 167.193 29.1492 167.193 26.5312C167.193 23.9272 168.817 22.2612 171.365 22.2612C173.899 22.2612 175.509 23.9132 175.509 26.5032C175.509 29.0932 173.871 30.8012 171.379 30.8012ZM171.365 23.7172C169.895 23.7172 168.999 24.7812 168.999 26.5312C168.999 28.2532 169.895 29.3172 171.351 29.3172C172.807 29.3172 173.703 28.2672 173.703 26.5592C173.703 24.7532 172.849 23.7172 171.365 23.7172ZM176.74 31.8232L178.14 30.8572C178.686 31.6132 179.302 31.9212 180.31 31.9212C181.388 31.9212 182.214 31.5432 182.606 30.8712C182.844 30.4512 182.942 29.9612 182.97 29.0092C182.27 29.8912 181.444 30.2832 180.226 30.2832C178 30.2832 176.572 28.7572 176.572 26.3632C176.572 23.9412 178.042 22.3732 180.31 22.3732C181.556 22.3732 182.382 22.7512 182.984 23.6052C182.956 23.3252 182.956 23.2972 182.956 23.1432V22.4992H184.832C184.762 23.1012 184.734 23.4932 184.734 24.2492V28.9392C184.734 30.6192 184.37 31.6972 183.558 32.3832C182.858 32.9572 181.696 33.2792 180.254 33.2792C178.616 33.2792 177.692 32.9012 176.74 31.8232ZM178.406 26.4052C178.406 27.2312 178.63 27.9172 179.05 28.3372C179.4 28.7152 180.002 28.9672 180.534 28.9672C181.304 28.9672 182.088 28.5192 182.48 27.8612C182.746 27.4272 182.872 26.9092 182.872 26.2652C182.872 25.3692 182.592 24.6692 182.032 24.1932C181.64 23.8712 181.178 23.6892 180.674 23.6892C179.274 23.6892 178.406 24.7392 178.406 26.4052ZM186.601 19.3912H188.631C188.561 19.9372 188.533 20.3852 188.533 21.2252V28.7152C188.533 29.4572 188.561 29.9192 188.631 30.5352H186.601C186.671 29.9332 186.699 29.5272 186.699 28.7152V21.2112C186.699 20.3712 186.671 19.9232 186.601 19.3912ZM197.934 26.9792H191.844C191.914 27.7492 192.04 28.1412 192.362 28.5472C192.768 29.0512 193.398 29.3312 194.112 29.3312C194.798 29.3312 195.386 29.0792 195.792 28.6312C195.96 28.4352 196.058 28.2812 196.198 27.9032L197.85 28.4352C197.528 29.1072 197.346 29.3732 196.996 29.7232C196.268 30.4372 195.288 30.8012 194.112 30.8012C191.634 30.8012 190.066 29.1492 190.066 26.5312C190.066 23.9412 191.62 22.2472 194.014 22.2472C195.652 22.2472 196.94 23.0592 197.5 24.4592C197.766 25.1312 197.864 25.6352 197.906 26.5872C197.906 26.6152 197.906 26.6572 197.934 26.9792ZM191.886 25.5932H196.114C196.016 24.9772 195.848 24.5992 195.498 24.2492C195.12 23.8712 194.644 23.6892 193.986 23.6892C192.894 23.6892 192.096 24.4172 191.886 25.5932Z"
                    fill="black"
                  />
                </svg>
              </a>
            </div>

            <TextInput
              name="email"
              label={t('mypage.email')}
              registerOptions={{ required: true }}
              placeholder="xxxxxxx@xxxxx.com"
              errorMessage={
                errors.email !== undefined ? t('mypage.requireHalf') : undefined
              }
              className={style.inputContainer}
            />

            <TextInput
              name="password"
              label={t('mypage.password')}
              registerOptions={{ required: true }}
              type="password"
              placeholder="xxxxxxxxxxx"
              note={t('mypage.requirePass')}
              errorMessage={
                errors.password !== undefined
                  ? t('mypage.requirePassLong')
                  : undefined
              }
              className={style.inputContainer}
            />

            <div className={style.buttonContainer}>
              {type === 'signup' ? (
                <>
                  <div className={style.question}>
                    <input
                      type="checkbox"
                      id="subscribe"
                      {...register('subscribe')}
                    />
                    <CheckboxIcon checked={subscribe} />
                    <label htmlFor="subscribe">
                      {t('mypage.wantSubscribe')}
                    </label>
                  </div>
                  <div className={style.question}>
                    <input
                      type="checkbox"
                      id="agree"
                      {...register('agree', { required: true })}
                    />
                    <CheckboxIcon checked={agree} />
                    <label htmlFor="agree">
                      {locale === 'ja' ? (
                        <>
                          <Link to="/terms" target="_blank">
                            利用規約
                          </Link>
                          と
                          <Link to="/privacy" target="_blank">
                            プライバシーポリシー
                          </Link>
                          に同意します
                        </>
                      ) : (
                        <>
                          I agree to the and{' '}
                          <Link to="/terms" target="_blank">
                            terms of use
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" target="_blank">
                            privacy policy
                          </Link>
                        </>
                      )}
                    </label>
                  </div>
                  {errors.agree && (
                    <div className={style.errorMessage}>
                      {t('mypage.requireAgreePolicy')}
                    </div>
                  )}
                  {errorMessage !== '' && (
                    <div className={style.errorMessage}>{errorMessage}</div>
                  )}
                  <Button
                    design="dark"
                    type="submit"
                    hover="opacity"
                    loading={loading}
                  >
                    {t('mypage.regist')}
                  </Button>
                  <Link to="/signin">
                    <Button design="white" hover="opacity">
                      {t('mypage.loginHere')}
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  {errorMessage !== '' && (
                    <div className={style.errorMessage}>{errorMessage}</div>
                  )}
                  <button
                    type="button"
                    className={style.forget}
                    onClick={() => setShowResetPasswordModal(true)}
                  >
                    {t('mypage.forgetPassword')}
                  </button>
                  <Button
                    design="dark"
                    type="submit"
                    hover="opacity"
                    loading={loading}
                  >
                    {t('mypage.login')}
                  </Button>
                  <Link to="/signup">
                    <Button design="white" hover="opacity">
                      {t('mypage.registrationHere')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </Modal>
      <MailConfirmModal
        show={state === 'confirmMail'}
        mail={email}
        onSubmit={confirmedMail}
      />
      <ResetPasswordModal
        show={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </>
  )
}

type ResetPasswordModalProps = {
  show: boolean
  onClose: () => void
}
const ResetPasswordModal = ({ show, onClose }: ResetPasswordModalProps) => {
  const { action } = useContext(StoreContext)
  const { t } = useTranslation()

  const [state, setState] = useState<
    'email' | 'code' | 'password' | 'complete'
  >('email')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const emailMethods = useForm<{ email: string }>()
  const email = emailMethods.watch('email')
  const emailSubmit = async (data: { email: string }) => {
    setLoading(true)
    try {
      await forgetPassword(data.email)
    } catch (error) {
      sendError(error)
      action.addToasts(t('mypage.failRequest'))
      setLoading(false)
      return
    }

    setLoading(false)
    setState('code')
  }

  const codeMethods = useForm<{ code: string }>()
  const code = codeMethods.watch('code')
  const codeSubmit = async () => {
    setLoading(true)
    // 一秒待ってそれっぽく振る舞う
    await sleep(1000)
    setState('password')
    setLoading(false)
  }
  const resendCode = async () => {
    setResending(true)
    try {
      await forgetPassword(email)
    } catch (error) {
      sendError(error)
      action.addToasts(t('mypage.failRequest'))
    }

    setResending(false)
  }

  const passwordMethods = useForm<{ password: string }>()
  const passwordSubmit = async (data: { password: string }) => {
    setLoading(true)
    try {
      await resetPassword(email, code, data.password)
    } catch (error) {
      try {
        await forgetPassword(email)
      } catch (error) {
        sendError(error)
        action.addToasts(t('mypage.failRequest'))
        return
      }
      action.addToasts(t('mypage.failCode'))
      setState('code')
      setLoading(false)
      return
    }

    setLoading(false)
    setState('complete')
  }

  return (
    <Modal show={show} type="overlay">
      <div className={style.resetPassword}>
        {state === 'email' && (
          <FormProvider {...emailMethods}>
            <form onSubmit={emailMethods.handleSubmit(emailSubmit)}>
              <div className={style.title}>{t('mypage.resetPassword')}</div>
              <div className={style.text}>
                {t('mypage.sendCodeForPassword')}
              </div>

              <TextInput
                name="email"
                label={t('mypage.email')}
                registerOptions={{ required: true }}
                placeholder="xxxxxxx@xxxxx.com"
                errorMessage={
                  emailMethods.formState.errors.email !== undefined
                    ? t('mypage.correctEmail')
                    : undefined
                }
                className={style.input}
                design="gray"
              />

              <div className={style.buttonContainer}>
                <Button design="white" onClick={onClose}>
                  {t('mypage.cancel')}
                </Button>
                <Button type="submit" loading={loading}>
                  {t('mypage.next')}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
        {state === 'code' && (
          <FormProvider {...codeMethods}>
            <form onSubmit={codeMethods.handleSubmit(codeSubmit)}>
              <div className={style.title}>{t('mypage.inputCode')}</div>
              <div className={style.email}>{email}</div>
              <div className={style.text}>{t('mypage.sendCode')}</div>

              <TextInput
                name="code"
                label={t('mypage.code')}
                registerOptions={{ required: true, maxLength: 6, minLength: 6 }}
                errorMessage={
                  codeMethods.formState.errors.code !== undefined
                    ? t('mypage.correctCode')
                    : undefined
                }
                className={style.input}
                design="code"
              />

              <div className={style.resent}>
                {t('mypage.noCode')}
                <button
                  type="button"
                  onClick={resendCode}
                  data-loading={resending}
                  disabled={resending}
                >
                  {t('mypage.noCodeResend')}
                </button>
              </div>

              <div className={style.buttonContainer}>
                <Button type="submit" loading={loading}>
                  {t('mypage.verify')}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
        {state === 'password' && (
          <FormProvider {...passwordMethods}>
            <form onSubmit={passwordMethods.handleSubmit(passwordSubmit)}>
              <div className={style.title}>{t('mypage.resetPassword')}</div>
              <div className={style.text}>{t('mypage.setPassword')}</div>

              <TextInput
                name="password"
                label={t('mypage.newPassword')}
                type="password"
                placeholder={t('mypage.requirePassShort')}
                registerOptions={{ required: true }}
                errorMessage={
                  codeMethods.formState.errors.code !== undefined
                    ? t('mypage.requirePassLong')
                    : undefined
                }
                className={style.input}
                design="gray"
              />

              <div className={style.buttonContainer}>
                <Button type="submit" loading={loading}>
                  {t('mypage.reset')}
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
        {state === 'complete' && (
          <>
            <div
              className={style.complete}
              dangerouslySetInnerHTML={{
                __html: t('mypage.resetPasswordSuccess'),
              }}
            />
            <div className={style.buttonContainer}>
              <Button
                onClick={async () => {
                  onClose()
                  await sleep(500)
                  setState('email')
                }}
              >
                {t('mypage.close')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default AuthForm
